import {
    PublicPriorityEmailAction,
    usePublicPriorityEmailRecordQuery,
    useUpdatePublicPriorityEmailRecordMutation
} from "@therapprove/common";
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogProps } from "../components/Dialog";
import { getDialogInitialState } from "../utils/initialState.utils";
import { queueLandingBase64ToPayload } from "../utils/payload.utils";

export const QueueLanding = () => {
    // Get the 'key' query string parameter from the URL.
    const urlParams = new URLSearchParams(window.location.search);
    const key = urlParams.get("key");

    // Mutations
    const [, updatePriorityEmailRecord] =
        useUpdatePublicPriorityEmailRecordMutation();

    const [dialog, setDialog] = useState<DialogProps>({
        title: "",
        description: "",
        icon: "",
        buttons: [],
        isLoading: false,
        showAgreement: false,
        successMessage: "",
        failureMessage: "",
        isSuccessful: null
    });

    // Use useMemo for the payload parsing.
    const decodedKey = useMemo(() => {
        if (!key) return undefined;
        try {
            const pl = queueLandingBase64ToPayload(key);
            if (!pl) {
                setDialog({
                    title: "Sorry, that link is invalid, and we couldn't process it successfully.",
                    description:
                        "Please try again, or contact support@therapprove.com if you believe this is an error.",
                    icon: "undo",
                    showAgreement: false,
                    isSuccessful: false
                });
                return undefined;
            }

            return pl;
        } catch (e) {
            console.error(e);
            return undefined;
        }
    }, [key]);

    // Query to get the info for the priority email record
    const [{ data: priorityEmailRecordResult }] =
        usePublicPriorityEmailRecordQuery({
            variables: {
                key1: decodedKey?.key1 || "",
                key2: decodedKey?.key2 || ""
            },
            pause: !decodedKey?.key1 || !decodedKey?.key2
        });

    const emailRecord =
        priorityEmailRecordResult?.priorityEmailRecords?.priorityEmailRecord;

    // Initialize the page values.
    useEffect(() => {
        if (
            priorityEmailRecordResult?.priorityEmailRecords?.priorityEmailRecord
        ) {
            const emailRecord =
                priorityEmailRecordResult.priorityEmailRecords
                    .priorityEmailRecord;

            if (emailRecord) {
                const { priorityEmailType, organizationName, patientName } =
                    emailRecord;

                const dialogInitialState = getDialogInitialState(
                    priorityEmailType,
                    organizationName,
                    patientName
                );

                setDialog({
                    ...dialog,
                    ...dialogInitialState,
                    buttons: dialogInitialState.buttons ?? []
                });
            } else {
                // Set the empty state
                setDialog({
                    ...dialog,
                    title: "No invitation found",
                    description: `We couldn't find the invitation you're looking for, it either doesn't exist, or may be expired.
            Please check your email link or contact support@therapprove.com if you believe this is an error.`,
                    icon: "error",
                    showAgreement: false,
                    isSuccessful: false,
                    buttons: []
                });
            }
        }
    }, [priorityEmailRecordResult]);

    /**
     * Handles the submission of the dialog
     * @param action The action to take.
     */
    const handleSubmit = async (action: PublicPriorityEmailAction) => {
        if (!decodedKey) return;

        const response = await updatePriorityEmailRecord({
            input: {
                priorityEmailAction: action,
                key1: decodedKey.key1,
                key2: decodedKey.key2
            }
        });

        if (response.error) {
            setDialog({
                ...dialog,
                isSuccessful: false,
                failureMessage:
                    "Sorry, an error has occurred. Please try again, or contact support@therapprove.com if the issue persists."
            });
        } else {
            // Set the success message based on the action taken.
            switch (action) {
                case PublicPriorityEmailAction.PriorityGlobalInviteAccept:
                    // NOTE: We don't care about the intake form url here because on the 'global invite' email,
                    // we're simply inviting them to global, not to a specific organization's queue.
                    setDialog({
                        title: `You have accepted this invitation.`,
                        description: `If you did not mean to accept, you can refresh this page and restart the process.`,
                        icon: "check",
                        showAgreement: false,
                        isSuccessful: true,
                        successMessage:
                            "You're now connected with Therapprove!",
                        buttons: []
                    });
                    break;
                case PublicPriorityEmailAction.PriorityGlobalInviteDecline:
                    setDialog({
                        title: `You have declined this invitation.`,
                        description: `If you did not mean to decline, you can refresh this page and restart the process.`,
                        icon: "block",
                        showAgreement: false,
                        isSuccessful: true,
                        successMessage: `You have declined the invitation from ${emailRecord?.organizationName}.`,
                        buttons: []
                    });
                    break;
                case PublicPriorityEmailAction.PriorityOrganizationInviteAccept:
                    if (
                        emailRecord?.intakeFormUrl &&
                        emailRecord.organizationName
                    ) {
                        setDialog({
                            title: "You have accepted this invitation.",
                            description: `Get started by completing ${emailRecord.organizationName}'s onboarding intake form!`,
                            icon: "description",
                            showAgreement: false,
                            isSuccessful: null,
                            buttons: [
                                {
                                    text: "Start Onboarding",
                                    variant: "primary",
                                    onClick: () => {
                                        if (emailRecord?.intakeFormUrl) {
                                            window.location.assign(
                                                emailRecord.intakeFormUrl
                                            );
                                        }
                                    }
                                }
                            ]
                        });
                        break;
                    }
                    setDialog({
                        title: `You have accepted this invitation.`,
                        description: `If you did not mean to accept, you can close this window, and click the link in your email to restart the process.`,
                        icon: "check",
                        showAgreement: false,
                        isSuccessful: true,
                        successMessage: `🚀 You've accepted ${emailRecord?.organizationName}'s invitation!`,
                        buttons: []
                    });
                    break;
                case PublicPriorityEmailAction.PriorityOrganizationInviteDecline:
                    setDialog({
                        title: `You have declined this invitation.`,
                        description: `If you did not mean to decline, you can close this window, and click the link in your email to restart the process.`,
                        icon: "block",
                        showAgreement: false,
                        isSuccessful: true,
                        successMessage: `You have declined the invitation from ${emailRecord?.organizationName}.`,
                        buttons: []
                    });
                    break;
                default:
                    setDialog({
                        title: "An error has occurred.",
                        description:
                            "Please try again, or contact support@therapprove.com if the issue persists.",
                        icon: "error",
                        showAgreement: false,
                        isSuccessful: false,
                        successMessage:
                            "Sorry, an error has occurred. Please try again, or contact support@therapprove.com if the issue persists.",
                        buttons: []
                    });
                    break;
            }
        }
    };

    return <Dialog {...dialog} onEmailRecordClick={handleSubmit} />;
};
